/* In-memory mocked client for Recipe V2 — no real backend exists for this domain yet. */

import { IRecipeV2Client } from './IRecipeV2Client';
import {
  mockRecipesV2,
  mockRecipeV2Details,
  mockAllergens,
  mockDietaryAttributes,
  mockProductionMethods,
  mockStockItems,
  mockSupplierComparisons,
} from '../../../../mocks/data/recipeV2Data';
import {
  RecipeV2,
  RecipeV2Detail,
  RecipeAudienceDetail,
  RecipeV2ListResponse,
  RecipeComponent,
  CreateRecipeRequest,
  CloneRecipeRequest,
  ApproveRejectRequest,
  AllergenOverride,
  CostSimulationResult,
  ComponentSupplierComparison,
  StockItemSearchResult,
  ProductionMethod,
  Allergen,
  RecipeStep,
  CreateUpdateStepRequest,
  UpdateComponentRequest,
  UpdateRecipeMetadataRequest,
  DietaryAttribute,
  DietaryAttributeOverride,
  DietaryAttributeReference,
  NutritionOverride,
  NutrientReference,
  RecipeBulkImportRun,
  RecipeStatus,
  RecipeKind,
  ConvertToStockRequest,
  ConvertToStockResult,
  RecipeVersionHistory,
  RecipeVersionDetail,
  RecipeExportInformationProfile,
  RecipeExportPresentationStyle,
} from './recipeV2Types';

const buildFallbackRecipeDetail = (id: number): RecipeV2Detail | null => {
  const recipe = mockRecipesV2.find((r) => r.id === id);
  if (!recipe) return null;

  const productionMethod =
    recipe.productionMethodId === null
      ? null
      : (mockProductionMethods.find((m) => m.id === recipe.productionMethodId) ?? null);

  return {
    ...recipe,
    components: [],
    allergens: recipe.allergenSummary,
    versions: [],
    aiFindings: [],
    carbonTotal: recipe.totalCost,
    carbonPerPortion: recipe.costPerPortion,
    productionMethod,
    steps: [],
  };
};

export class RecipeV2ClientMocked implements IRecipeV2Client {
  async getRecipeUsage(): Promise<import('./recipeV2Types').RecipeMenuUsage[]> { return []; }
  async getRecipes(search: string, status: string, category: string, page: number, pageSize: number): Promise<RecipeV2ListResponse> {
    let filtered: RecipeV2[] = mockRecipesV2;

    const term = search?.toLowerCase();
    if (term) {
      filtered = filtered.filter(
        (r) =>
          r.description.toLowerCase().includes(term) ||
          r.itemNumber.toLowerCase().includes(term) ||
          r.primaryCategory.toLowerCase().includes(term),
      );
    }
    if (status) {
      filtered = filtered.filter((r) => r.status === status);
    }
    if (category) {
      filtered = filtered.filter((r) => r.primaryCategory === category);
    }

    const start = (page - 1) * pageSize;
    const data = filtered.slice(start, start + pageSize);

    return { data, totalCount: filtered.length, page, pageSize };
  }

  async getRecipeBankList(
    search: string,
    category: string,
    dietaryAttributeIds: number[],
    page: number,
    pageSize: number,
  ): Promise<RecipeV2ListResponse> {
    let filtered = mockRecipesV2.filter((recipe) => recipe.status === RecipeStatus.Active);

    const term = search?.toLowerCase();
    if (term) {
      filtered = filtered.filter((recipe) =>
        recipe.description.toLowerCase().includes(term)
        || recipe.itemNumber.toLowerCase().includes(term)
        || recipe.primaryCategory.toLowerCase().includes(term));
    }
    if (category) {
      filtered = filtered.filter((recipe) => recipe.primaryCategory === category);
    }

    const requested = new Set(dietaryAttributeIds);
    if (requested.size > 0) {
      filtered = filtered.filter((recipe) => (recipe.dietaryAttributes ?? []).some((attribute) =>
        requested.has(attribute.id)
        && String(attribute.status).replace(/[\s_-]/g, '').toLowerCase() !== 'notsuitable'));
    }

    const start = (page - 1) * pageSize;
    return {
      data: filtered.slice(start, start + pageSize),
      totalCount: filtered.length,
      page,
      pageSize,
    };
  }

  async getRecipeBankDetail(id: number): Promise<RecipeV2Detail> {
    const detail = await this.getRecipeDetail(id);
    if (detail.status !== RecipeStatus.Active) throw new Error(`Recipe ${id} is not published.`);
    return detail;
  }

  async getRecipePublishingDetail(id: number): Promise<RecipeV2Detail> {
    return this.getRecipeBankDetail(id);
  }

  async getRecipeAudienceDetail(id: number): Promise<RecipeAudienceDetail> {
    const recipe = await this.getRecipeBankDetail(id);
    return {
      id: recipe.id,
      franchiseId: recipe.franchiseId,
      itemNumber: recipe.itemNumber,
      description: recipe.description,
      narrativeDescription: recipe.narrativeDescription,
      primaryCategory: recipe.primaryCategory,
      mediaUrl: recipe.mediaUrl,
      recipeVersionId: recipe.recipeVersionId,
      recipePublicationId: recipe.recipePublicationId,
      complianceRevisionIds: recipe.complianceRevisionIds ?? [],
      currentVersion: recipe.currentVersion,
      nutrition: recipe.nutrition ?? [],
      allergenSummary: recipe.allergenSummary ?? [],
      dietaryAttributes: recipe.dietaryAttributes ?? [],
    };
  }

  async getRecipeDetail(id: number): Promise<RecipeV2Detail> {
    const detail = mockRecipeV2Details[id] ?? buildFallbackRecipeDetail(id);
    if (!detail) throw new Error(`Recipe ${id} not found`);
    return detail;
  }

  async getVersionHistory(_id: number): Promise<RecipeVersionHistory[]> {
    throw new Error('Recipe version history requires the production Recipe V2 API.');
  }

  async getVersionDetail(_id: number, _versionNumber: number): Promise<RecipeVersionDetail> {
    throw new Error('Recipe version detail requires the production Recipe V2 API.');
  }

  async createRecipe(data: CreateRecipeRequest): Promise<RecipeV2> {
    return {
      id: 99,
      franchiseId: data.franchiseId,
      itemNumber: data.itemNumber,
      description: data.description,
      status: RecipeStatus.Draft,
      recipeType: RecipeKind.Standard,
      primaryCategory: data.primaryCategory,
      yields: data.yieldPortions == null
        ? []
        : [{
          id: -1,
          quantity: data.yieldPortions,
          uom: data.portionUom ?? 'portion',
          displayOrder: 0,
        }],
      productionMethodId: data.productionMethodId ?? null,
      yieldPortions: data.yieldPortions,
      portionQuantity: data.portionQuantity,
      portionUom: data.portionUom,
      expectedYieldLossPercent: data.expectedYieldLossPercent,
      consumptionMode: data.consumptionMode,
      productionTrigger: data.productionTrigger,
      storageMethod: data.storageMethod,
      shelfLifeValue: data.shelfLifeValue,
      shelfLifeUnit: data.shelfLifeUnit,
      carbonScore: null,
      totalCost: 0,
      costPerPortion: 0, costIsComplete: true, missingCostComponentItemNumbers: [],
      narrativeDescription: data.narrativeDescription ?? undefined,
      allergenSummary: [],
      dietaryAttributes: [],
      aiIssueCount: 0,
      currentVersion: 1,
      createdBy: 'Demo User',
      createdAt: new Date().toISOString(),
      modifiedBy: 'Demo User',
      modifiedAt: new Date().toISOString(),
    };
  }

  async updateRecipe(id: number, data: UpdateRecipeMetadataRequest): Promise<RecipeV2Detail> {
    const existing = mockRecipeV2Details[id] ?? buildFallbackRecipeDetail(id);
    if (!existing) throw new Error(`Recipe ${id} not found`);
    return {
      ...existing,
      description: data.description ?? existing.description,
      primaryCategory: data.primaryCategory ?? existing.primaryCategory,
      yields: data.yieldPortions == null
        ? []
        : [{
          id: -1,
          quantity: data.yieldPortions,
          uom: data.portionUom ?? 'portion',
          displayOrder: 0,
        }],
      yieldPortions: data.yieldPortions,
      portionQuantity: data.portionQuantity,
      portionUom: data.portionUom,
      expectedYieldLossPercent: data.expectedYieldLossPercent,
      consumptionMode: data.consumptionMode,
      productionTrigger: data.productionTrigger,
      storageMethod: data.storageMethod,
      shelfLifeValue: data.shelfLifeValue,
      shelfLifeUnit: data.shelfLifeUnit,
      productionMethodId: data.productionMethodId ?? existing.productionMethodId,
      narrativeDescription: data.narrativeDescription ?? existing.narrativeDescription,
      modifiedBy: 'Demo User',
      modifiedAt: new Date().toISOString(),
    };
  }

  async deleteRecipe(id: number): Promise<RecipeV2> {
    const existing = mockRecipesV2.find(r => r.id === id);
    if (!existing) throw new Error('Recipe not found.');
    existing.status = RecipeStatus.Archived;
    return existing;
  }

  async finishRecipe(id: number): Promise<RecipeV2> {
    const existing = mockRecipeV2Details[id] ?? buildFallbackRecipeDetail(id);
    if (!existing) throw new Error(`Recipe ${id} not found`);
    return { ...existing, modifiedBy: 'Demo User', modifiedAt: new Date().toISOString() };
  }

  async cloneRecipe(id: number, data: CloneRecipeRequest): Promise<RecipeV2> {
    const source = mockRecipesV2.find((r) => r.id === id);
    if (!source) throw new Error(`Recipe ${id} not found`);
    return { ...source, id: 100, itemNumber: data.itemNumber, description: data.description, status: RecipeStatus.Draft, currentVersion: 1 };
  }

  async submitRecipe(_id: number, _reason?: string): Promise<void> {
    throw new Error('Recipe governance submit requires the production Recipe V2 API.');
  }

  async approveRecipe(_id: number, _data: ApproveRejectRequest): Promise<void> {
    throw new Error('Recipe governance approval requires the production Recipe V2 API.');
  }

  async rejectRecipe(_id: number, _data: ApproveRejectRequest): Promise<void> {
    throw new Error('Recipe governance rejection requires the production Recipe V2 API.');
  }

  async rollbackVersion(_id: number, _versionNumber: number, _reason?: string): Promise<number> {
    throw new Error('Recipe governance rollback requires the production Recipe V2 API.');
  }

  async addComponent(
    recipeId: number,
    component: Omit<UpdateComponentRequest, 'sortOrder'> & { componentProductId: number; componentItemNumber: string; componentDescription: string; sortOrder?: number },
  ): Promise<RecipeComponent> {
    return {
      id: Math.floor(Math.random() * 100000),
      recipeId,
      componentProductId: component.componentProductId,
      componentItemNumber: component.componentItemNumber,
      componentDescription: component.componentDescription,
      quantity: component.quantity,
      weight: component.weight ?? null,
      volume: component.volume ?? 0,
      sortOrder: component.sortOrder ?? 0,
      uom: component.uom,
      costPrice: 0,
      costContribution: 0,
      weightNet: component.weight ?? 0,
      isRecipe: false,
    };
  }

  async updateComponent(recipeId: number, componentId: number, data: UpdateComponentRequest): Promise<RecipeComponent> {
    return {
      id: componentId,
      recipeId,
      componentProductId: 0,
      componentItemNumber: '',
      componentDescription: '',
      quantity: data.quantity,
      weight: data.weight ?? null,
      volume: data.volume ?? 0,
      sortOrder: data.sortOrder,
      uom: data.uom,
      costPrice: 0,
      costContribution: 0,
      weightNet: data.weight ?? 0,
      isRecipe: false,
    };
  }

  async removeComponent(_recipeId: number, _componentId: number): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async reorderComponents(_recipeId: number, _componentIds: number[]): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async convertToStock(_recipeId: number, _request: ConvertToStockRequest): Promise<ConvertToStockResult> {
    throw new Error('Produced-stock production is not available in the mocked Recipe V2 client.');
  }

  async getSteps(recipeId: number): Promise<RecipeStep[]> {
    const detail = mockRecipeV2Details[recipeId];
    return detail?.steps ?? [];
  }

  async addStep(recipeId: number, step: CreateUpdateStepRequest): Promise<RecipeStep> {
    const existingSteps = mockRecipeV2Details[recipeId]?.steps ?? [];
    return {
      id: Math.floor(Math.random() * 100000),
      recipeId,
      stepNumber: step.stepNumber ?? existingSteps.length + 1,
      title: step.title,
      instruction: step.instruction,
      productionMethodId: step.productionMethodId,
      durationMinutes: step.durationMinutes,
      temperature: step.temperature,
      equipment: [],
    };
  }

  async updateStep(recipeId: number, stepId: number, step: CreateUpdateStepRequest): Promise<RecipeStep> {
    const existing = mockRecipeV2Details[recipeId]?.steps?.find(s => s.id === stepId);
    return {
      id: stepId,
      recipeId,
      stepNumber: step.stepNumber ?? existing?.stepNumber ?? 1,
      title: step.title,
      instruction: step.instruction,
      productionMethodId: step.productionMethodId,
      durationMinutes: step.durationMinutes,
      temperature: step.temperature,
      equipment: [],
    };
  }

  async removeStep(_recipeId: number, _stepId: number): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async reorderSteps(_recipeId: number, _stepIds: number[]): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async getRecipeMediaUrl(_recipeId: number, _stepId?: number): Promise<string | null> { return null; }
  async authorizeRecipeMediaUpload(_recipeId: number, _file: File, _stepId?: number): Promise<import('./recipeV2Types').RecipeMediaUploadAuthorization> { throw new Error('Recipe media is unavailable in mocked mode.'); }
  async finalizeRecipeMediaUpload(_recipeId: number, _uploadId: string): Promise<void> { throw new Error('Recipe media is unavailable in mocked mode.'); }
  async removeRecipeMedia(_recipeId: number, _stepId?: number): Promise<void> { throw new Error('Recipe media is unavailable in mocked mode.'); }

  async getAllergens(_franchiseId?: number): Promise<Allergen[]> {
    return mockAllergens;
  }

  async overrideAllergen(_recipeId: number, _override: AllergenOverride): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async clearAllergenOverride(_recipeId: number, _allergenId: number, _reason: string): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async getDietaryAttributes(recipeId: number): Promise<DietaryAttribute[]> {
    const detail = mockRecipeV2Details[recipeId];
    return detail?.dietaryAttributes ?? [];
  }

  async getDietaryAttributeReference(_franchiseId?: number): Promise<DietaryAttributeReference[]> {
    const seen = new Map<number, DietaryAttributeReference>();
    Object.values(mockDietaryAttributes).flat().forEach(attribute => {
      if (!seen.has(attribute.id)) seen.set(attribute.id, { id: attribute.id, code: attribute.code, name: attribute.name, category: attribute.category });
    });
    return Array.from(seen.values());
  }

  async overrideDietaryAttribute(_recipeId: number, _override: DietaryAttributeOverride): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async clearDietaryAttributeOverride(_recipeId: number, _attributeId: number, _reason: string): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async getNutrients(_franchiseId?: number): Promise<NutrientReference[]> {
    const seen = new Map<string, NutrientReference>();
    Object.values(mockRecipeV2Details).flatMap(detail => detail.nutrition ?? []).forEach((nutrient, index) => {
      if (!seen.has(nutrient.code)) seen.set(nutrient.code, { nutrientId: index + 1, code: nutrient.code, name: nutrient.name });
    });
    return Array.from(seen.values());
  }

  async overrideNutrition(_recipeId: number, _override: NutritionOverride): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async clearNutritionOverride(_recipeId: number, _nutrientCode: string, _reason: string): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async getProductionMethods(_franchiseId?: number): Promise<ProductionMethod[]> {
    return mockProductionMethods;
  }

  async dismissAIFinding(_recipeId: number, _findingId: number, _reason: string): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async acknowledgeAIFinding(_recipeId: number, _findingId: number): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async simulateCost(_id: number, _componentId: number, priceChangePercent: number): Promise<CostSimulationResult> {
    const originalCost = 4.92;
    return {
      originalCost,
      simulatedCost: originalCost * (1 + priceChangePercent / 100),
      difference: originalCost * (priceChangePercent / 100),
      percentageChange: priceChangePercent,
    };
  }

  async getSupplierComparison(id: number): Promise<ComponentSupplierComparison[]> {
    return mockSupplierComparisons[id] ?? [];
  }

  async searchStockItems(search: string): Promise<StockItemSearchResult[]> {
    const term = search?.toLowerCase() ?? '';
    const stockMatches: StockItemSearchResult[] = mockStockItems
      .filter((i) => i.description.toLowerCase().includes(term) || i.itemNumber.toLowerCase().includes(term));
    // Recipes (sub-recipes) can be added as components too - search includes them alongside stock items.
    const recipeMatches: StockItemSearchResult[] = mockRecipesV2
      .filter((r) => r.description.toLowerCase().includes(term) || r.itemNumber.toLowerCase().includes(term))
      .map((r) => ({
        id: r.id,
        itemNumber: r.itemNumber,
        description: r.description,
        uom: r.yields[0]?.uom ?? '',
        costPrice: r.costPerPortion ?? 0,
        weightNet: 0,
        isRecipe: true,
      }));
    return [...recipeMatches, ...stockMatches].slice(0, 10);
  }

  async exportRecipes(_search: string, _status: string, _category: string, format: 'csv' | 'xlsx'): Promise<Blob> {
    return new Blob(['mock,csv,data'], { type: format === 'csv' ? 'text/csv' : 'application/vnd.ms-excel' });
  }

  async exportRecipeDocument(_id: number, _informationProfile: RecipeExportInformationProfile, _presentationStyle: RecipeExportPresentationStyle): Promise<Blob> {
    throw new Error('Recipe document export requires production API.');
  }

  async exportBulkWorkbook(_franchiseId: number): Promise<Blob> {
    throw new Error('Recipes V2 bulk management requires the production Range API.');
  }

  async validateBulkWorkbook(_franchiseId: number, _idempotencyKey: string, _file: File): Promise<RecipeBulkImportRun> {
    throw new Error('Recipes V2 bulk management requires the production Range API.');
  }

  async commitBulkWorkbook(_runId: number, _franchiseId: number, _file: File): Promise<RecipeBulkImportRun> {
    throw new Error('Recipes V2 bulk management requires the production Range API.');
  }

  async getBulkImportRun(_runId: number): Promise<RecipeBulkImportRun> {
    throw new Error('Recipes V2 bulk management requires the production Range API.');
  }
}
